import {vatPercentToLabel} from "@/helpers/vat";
import {normalizeName} from "@/helpers/import/matchNames";
import {REUSE} from "@/helpers/import/resolveImportGroups";

function validRows(sheets, name) {
    return (sheets[name]?.rows || []).filter(row => !row.excluded);
}

function localized(values, prefix) {
    return Object.fromEntries(['fr', 'nl', 'en']
        .map(locale => [locale, values[`${prefix}${locale.charAt(0).toUpperCase()}${locale.slice(1)}`]])
        .filter(([, value]) => value));
}

function orNull(object) {
    return Object.keys(object || {}).length ? object : null;
}

function vatFields(vatIn, vatOut, blackbox) {
    return blackbox
        ? {vatIn, vatOut, vatLabelIn: vatPercentToLabel(vatIn), vatLabelOut: vatPercentToLabel(vatOut)}
        : {vatIn, vatOut};
}

function groupPayload(group, groupRefs, blackbox, position) {
    const ref = groupRefs.get(group.key);
    if (group.status === REUSE) return {ref, productGroupId: group.productGroupId};
    const {settings, values} = group;
    return {
        ref,
        parentRef: group.parentKey ? groupRefs.get(group.parentKey) : null,
        name: group.name,
        ...vatFields(values.vatIn, values.vatOut ?? values.vatIn, blackbox),
        color: values.color || null,
        orderIndex: settings.orderIndex ?? position,
        userHidden: !!settings.userHidden,
        webHidden: !!settings.webHidden,
        kioskHidden: !!settings.kioskHidden,
        imageUrl: settings.imageUrl || null,
        externalReference: settings.externalReference || null,
        maxChoiceFree: settings.maxChoiceFree ?? 0,
        maxChoicePaid: settings.maxChoicePaid ?? 0,
        forceChoiceAmountFree: settings.forceChoiceAmountFree ?? 0,
        forceChoiceAmountPaid: settings.forceChoiceAmountPaid ?? 0,
        printers: [],
    };
}

function productRef(rowNumber) {
    return `p${rowNumber}`;
}

export function buildImportPayload({sheets, groups, resolveGroup, lists, resolveList, selectedRows, blackbox, locale}) {
    const groupRefs = new Map(groups.map((group, index) => [group.key, `g${index + 1}`]));
    const groupsByKey = new Map(groups.map(group => [group.key, group]));
    const listRefs = new Map(lists.map((list, index) => [list.key, `l${index + 1}`]));
    const groupKeyOf = path => normalizeName(resolveGroup(path));

    const products = validRows(sheets, 'Products')
        .filter(row => selectedRows.has(row.rowNumber) && groupsByKey.has(groupKeyOf(row.values.group)))
        .map(row => {
            const values = row.values;
            const groupKey = groupKeyOf(values.group);
            const group = groupsByKey.get(groupKey);
            const vatIn = values.vatIn ?? group.values.vatIn;
            const vatOut = values.vatOut ?? values.vatIn ?? group.values.vatOut ?? vatIn;
            const optionGroups = (paths, optionType) => (paths || [])
                .map(path => groupKeyOf(path))
                .filter(key => key !== groupKey && groupRefs.has(key))
                .map(key => ({groupRef: groupRefs.get(key), optionType, multiOptionAllowed: false}));
            const optionListRefs = [...new Set((values.optionLists || [])
                .map(name => resolveList(name))
                .filter(Boolean)
                .map(list => listRefs.get(list.key))
                .filter(Boolean))];
            return {
                ref: productRef(row.rowNumber),
                groupRef: groupRefs.get(groupKey),
                name: values.name,
                priceVatInclIn: values.dynamicPrice ? 0 : values.priceVatInclIn,
                priceVatInclOut: values.dynamicPrice ? 0 : values.priceVatInclOut ?? values.priceVatInclIn,
                ...vatFields(vatIn, vatOut, blackbox),
                productBarcodes: (values.barcodes || []).map(code => Number(code)),
                description: orNull(localized(values, 'description')),
                color: values.color || null,
                imageUrl: values.imageUrl || null,
                externalReference: values.externalReference || null,
                purchasePriceVatExcl: values.purchasePriceVatExcl ?? null,
                orderIndex: values.orderIndex ?? row.rowNumber,
                userHidden: !!values.userHidden,
                webHidden: !!values.webHidden,
                kioskHidden: !!values.kioskHidden,
                dynamicPrice: !!values.dynamicPrice,
                weightPrice: !!values.weightPrice,
                optionGroups: [
                    ...optionGroups(values.freeOptionGroups, 'FREE'),
                    ...optionGroups(values.paidOptionGroups, 'PAID'),
                ],
                optionListRefs,
            };
        });

    const importedRows = new Set(products.map(product => product.ref));
    const linkedListRefs = new Set(products.flatMap(product => product.optionListRefs));
    const keptLists = lists.filter(list => {
        const hadContent = list.items.length > 0 || list.productRows.size > 0;
        const keepsItems = list.items.some(item => importedRows.has(productRef(item.productRow)));
        return !hadContent || keepsItems || linkedListRefs.has(listRefs.get(list.key));
    });
    const optionLists = keptLists.map(list => ({
        ref: listRefs.get(list.key),
        nameLocalized: {[locale]: list.name, ...list.nameLocalized},
        description: orNull(list.description),
        minChoiceTotal: list.values.minChoice ?? null,
        maxChoiceTotal: list.values.maxChoice ?? null,
        forceChoiceTotal: list.values.forceChoice ?? null,
        items: list.items
            .filter(item => importedRows.has(productRef(item.productRow)))
            .map(item => ({
                productRef: productRef(item.productRow),
                free: item.free,
                allowMultipleChoice: item.allowMultipleChoice,
                includeOptionLists: item.includeOptionLists,
                maxChoice: item.maxChoice,
                type: item.without ? 'WITHOUT' : null,
                priceOverride: item.priceOverride,
            })),
    }));

    const positions = new Map();
    const groupsPayload = groups.map(group => {
        const position = (positions.get(group.parentKey) || 0) + 1;
        positions.set(group.parentKey, position);
        return groupPayload(group, groupRefs, blackbox, position);
    });

    return {groups: groupsPayload, products, optionLists};
}
